<template>
  <div>
    <LabelRow
      v-if="props.label"
      :for="props.id"
      :label="props.label"
      :toolTipLabel="props.toolTipLabel"
    />

    <div class="input-row" :class="{ touched: touched, 'disabled-input': props.disabled  }">
      <span v-if="props.prefix" :class="{ 'disabled-input': props.disabled }" class="prefix">{{ props.prefix }}</span>
      <input
        :class="{ 'disabled-input': props.disabled }"
        :disabled="props.disabled"
        :id="props.id"
        :placeholder="props.placeholder"
        :type="props.type"
        :value="props.modelValue"
        @beforeinput="(event: Event) => onBeforeInput(event)"
        @input="(event: Event) => onInput(event)"
      >
      <slot name="suffix" />
      <div class="loading" v-if="props.loading">
        <svg class="spinner" viewBox="0 0 50 50">
          <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle>
        </svg>
      </div>
    </div>

    <ErrorList v-if="props.errors" :errors="props.errors" />
  </div>
</template>

<script setup lang="ts">
import {ErrorObject} from "@vuelidate/core";
import {computed, reactive} from "vue";
import LabelRow from "@/components/FormInputs/Partials/LabelRow.vue";
import ErrorList from "@/components/FormInputs/Partials/ErrorList.vue";

const emits = defineEmits<{
  'update:modelValue': [value: string];
}>();
const props = defineProps<{
  id: string;
  modelValue: string;
  type: string;
  disabled?: boolean;
  errors?: ErrorObject[];
  label?: string;
  max?: number;
  placeholder?: string;
  prefix?: string;
  toolTipLabel?: string;
  loading?: boolean;
}>();

const touched = computed(() => !!props.modelValue);

function onBeforeInput(event: Event) {
  if (!(event instanceof InputEvent && event.target instanceof HTMLInputElement)) return;

  if (props.max && event.data && (event.target.value + event.data).length > props.max) {
    event.preventDefault(); // Cancel 'input' event
  }
}

function onInput(event: Event) {
  const isUserInput = event instanceof InputEvent && event.target instanceof HTMLInputElement;
  const isAutofilled = !(event instanceof InputEvent) && event.target instanceof HTMLInputElement;

  if (!isUserInput && !isAutofilled) return;

  emits('update:modelValue', event.target.value);
}
</script>

<style scoped lang="scss">
@import "@/styles/form_input.scss";

input {
  background-color: #FFFFFF;
}

.loading {
  display: inline-block;
  margin: 10px 0 0 20px;
  height: 100%;
  color:#00586E;
}
.spinner {
  animation: rotate 2s linear infinite;
  z-index: 2;
  margin: 5px auto;
  width: 25px;
  height: 25px;

  & .path {
    stroke: #00586E;
    stroke-linecap: round;
    animation: dash 1.5s ease-in-out infinite;
  }

}

.disabled-input {
  background-color: #e0e0e0; /* Gray background */
  color: #a0a0a0; /* Gray text */
}


@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
}
</style>
